// @flow

import React, { PureComponent } from 'react';

import PageNotFound from 'components/app/PageNotFound';
import Breadcrumb from 'components/fragments/Breadcrumb';
import CardAlbum from 'components/fragments/card/CardAlbum'; // ! container sur les comités
import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import Loading from 'components/fragments/Loading';
import type { StateProps as Props } from 'components/page/PageList';
import BottomPages from 'components/page/template/PageMedia/BottomPages';
import Sidebar from 'components/sidebar/Sidebar';
import { STATUS_SUCCESS, STATUS_FAILURE, STATUS_UNKNOWN } from 'constants/statusConstants';
import BackgroundImage from 'containers/fragments/BackgroundImageContainer';
import LiveScore from 'containers/fragments/LiveScoreContainer';
import type { AlbumType } from 'types/Albums';
import { getDateFromString } from 'utils/dateUtils';

import './PageMedia.css';

type State = {
  page: number,
  sortOptionId: number,
  season: number,
  filter: number,
  filteredAlbums: AlbumType[]
};

export const PAGE_SIZE = 8;
const SORT_OPTIONS = [
  { value: 0, label: 'Trier par les plus récents' },
  { value: 1, label: 'Trier par les plus anciens' }
  //{ value: , label: 'les plus vus' },
  //{ value: , label: 'les plus partagés' },
];

export default class PageMedia extends PureComponent<Props, State> {
  state = {
    page: 0,
    sortOptionId: 0,
    season: 0,
    filter: 0,
    filteredAlbums: []
  };

  componentDidMount() {
    this.setFilteredAlbums();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { albums } = this.props;
    const { albums: prevAlbums } = prevProps;
    const { filter, season, sortOptionId } = this.state;
    const { filter: prevFilter, season: prevSeason, sortOptionId: prevSortOptionId } = prevState;

    if (albums !== prevAlbums || filter !== prevFilter || season !== prevSeason || sortOptionId !== prevSortOptionId) {
      this.setFilteredAlbums();
    }
  }

  //* SPECIFIC FUNCTIONS
  setFilteredAlbums = () => {
    const { albums } = this.props;
    const { filter, season, sortOptionId } = this.state;

    const filteredAlbums = albums
      .filter(album => (!filter || album.filtres_albums.includes(filter)) && (!season || album.saison.includes(season)))
      .sort((a, b) => {
        const aDate = getDateFromString(a.date);
        const bDate = getDateFromString(b.date);

        switch (sortOptionId) {
          case 0: // les plus récents
            return bDate.getTime() - aDate.getTime();

          case 1: // les plus anciens
          default:
            return aDate.getTime() - bDate.getTime();
        }
      });

    this.setState({ filteredAlbums });
  };

  getOptions = () => {
    const { albums_filtres, albums_saisons } = this.props;
    const newSeasons =
      albums_saisons && albums_saisons.length > 0
        ? albums_saisons.map<any>(saison => {
            return { label: saison.name, value: saison.term_id };
          })
        : [];
    const newFilters =
      albums_filtres && albums_filtres.length > 0
        ? albums_filtres.map<any>(filter => {
            return { label: filter.name, value: filter.term_id };
          })
        : [];

    return {
      seasonOptions: newSeasons,
      filtersOptions: newFilters
    };
  };

  getSetPage = (index: number) => () => this.setState({ page: index });

  handleSeasonChange = (value: string) => this.setState({ season: parseInt(value) });

  handleCompetitionChange = (value: string) => this.setState({ filter: parseInt(value) });

  handleSortChange = (value: string) => this.setState({ sortOptionId: parseInt(value) });

  sortAlbums = (value: number) => {
    return (a: AlbumType, b: AlbumType) => {
      const aDate = getDateFromString(a.date);
      const bDate = getDateFromString(b.date);

      switch (value) {
        case 0: // les plus récents
          return bDate.getTime() - aDate.getTime();

        case 1: // les plus anciens
        default:
          return aDate.getTime() - bDate.getTime();
      }
    };
  };

  //* RENDERS
  renderAlbumCards = (albums: AlbumType[]): React$Node => {
    return (
      albums &&
      albums.map(({ id, title, date, itemsCount, featured_media, slug }) => {
        return (
          <div key={`card_${id}`} className="col col-md-6 mb-4">
            <CardAlbum
              slug={slug}
              title={title}
              date={date}
              count={itemsCount}
              illustration={featured_media.src}
              srcset={featured_media.sizes}
            />
          </div>
        );
      })
    );
  };

  renderAlbumRow = (filteredAlbums: Array<any>) => {
    const { page } = this.state;
    const chunkedAlbums = filteredAlbums.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).reduce(
      (acc, curr) => {
        if (acc[acc.length - 1].length < 2) {
          acc[acc.length - 1].push(curr);
        } else {
          acc.push([curr]);
        }

        return acc;
      },
      [[]]
    );

    // $FlowFixMe
    return chunkedAlbums.map((albums, index) => {
      if (!albums || albums.length === 0) {
        return null;
      }

      return (
        <div key={albums[0].id} className="row">
          {this.renderAlbumCards(albums)}
        </div>
      );
    });
  };

  //* RENDER
  render() {
    const {
      albumsStatus,
      featured_media,
      banniere,
      title,
      excerpt,
      sidebar,
      rencontresLive,
      organization,
      albums,
      albums_filtres,
      albums_saisons
    } = this.props;
    const { filteredAlbums, page, sortOptionId } = this.state;

    if (albumsStatus === STATUS_UNKNOWN || albumsStatus === STATUS_FAILURE) {
      return <PageNotFound noMargin />;
    }
    if (albumsStatus !== STATUS_SUCCESS) {
      return <Loading />;
    }

    let bannerClass = 'banner';
    if (rencontresLive) {
      bannerClass = 'banner banner--live-score';
    }

    const { seasonOptions, filtersOptions } = this.getOptions();
    seasonOptions.push({ label: 'Toutes les saisons', value: 0 });
    filtersOptions.push({ label: 'Tous les filtres', value: 0 });

    return (
      <>
        <section className={bannerClass}>
          <BackgroundImage
            className="banner__thumbnail img-blue-dark"
            illustration={featured_media.src || banniere}
            baliseImg={false}
          />
          <div className="container">
            <Breadcrumb elements={[{ name: 'accueil', to: '/' }, title]} isDark />
            <h1 className="ft-h2">
              {title} - {organization}
            </h1>
            {excerpt && <p className="banner__desc">{excerpt}</p>}
          </div>
          <LiveScore />
        </section>
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                {/* @TODO : Header filter */}
                <div className="header-filter header-filter__sep mb-5">
                  <div className="left-block">
                    <div className="albums-number">
                      {filteredAlbums.length} Album
                      {filteredAlbums.length > 1 && 's'}
                    </div>
                    {albums_saisons && albums_saisons.length > 0 && (
                      <DropdownRadioInput
                        options={seasonOptions}
                        inputChanged={this.handleSeasonChange}
                        radio={true}
                        maj={true}
                        title={true}
                        noBorder={true}
                        label={
                          seasonOptions.find(({ value }) => value === this.state.season)?.label || 'Choisir une saison'
                        }
                      />
                    )}
                    {albums_filtres && albums_filtres.length > 0 && (
                      <DropdownRadioInput
                        options={filtersOptions}
                        inputChanged={this.handleCompetitionChange}
                        radio={true}
                        maj={true}
                        title={true}
                        noBorder={true}
                        label={
                          filtersOptions.find(({ value }) => value === this.state.filter)?.label || 'Choisir un filtre'
                        }
                      />
                    )}
                  </div>

                  <DropdownRadioInput
                    options={SORT_OPTIONS}
                    inputChanged={this.handleSortChange}
                    radio={true}
                    maj={true}
                    title={true}
                    noBorder={true}
                    label={SORT_OPTIONS.filter(option => option.value === sortOptionId)[0]?.label || ''}
                  />
                </div>

                <div className="row">
                  <div className="col col-lg-8on9">
                    {filteredAlbums.length > 0 ? (
                      this.renderAlbumRow(filteredAlbums)
                    ) : (
                      <div className="js-nofind">
                        <p className="ft-primary">Aucun album</p>

                        {albums.length > filteredAlbums.length && (
                          <p className="ft-secondary mb-6 is-grey">
                            Nous n&apos;avons trouvé aucun résultat pour votre recherche
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {filteredAlbums.length > 0 && (
                  <BottomPages albums={filteredAlbums} getSetPage={this.getSetPage} page={page} />
                )}
              </section>
            </div>
            <Sidebar sidebarContent={sidebar} />
          </div>
        </div>
      </>
    );
  }
}
